'use client'

import { useState, useEffect, useMemo } from 'react'
import { useTranslations } from 'next-intl'
import { useProjects } from '@/hooks/use-projects'
import { useSprints, useSprintsFromMultipleProjects } from '@/hooks/use-sprints'
import type { DbSprint } from '@/lib/types/db-sprint'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import { CreateSprintSidebar } from './create-sprint-sidebar'
import { EditSprintSidebar } from './edit-sprint-sidebar'
import { AddTaskToSprintModal } from './add-task-to-sprint-modal'
import { Plus, Calendar, Zap, Edit, CheckCircle2, PlayCircle, Clock, FolderOpen, Search, Eye, Trash2, UserPlus } from 'lucide-react'
import { format, differenceInDays } from 'date-fns'
import { cn } from '@/lib/utils'
import { EnhancedProject } from '@/hooks/use-projects'

interface SprintListViewProps {
  projectId?: number | null
  showProjectSelector?: boolean
}

interface SprintWithProject extends DbSprint {
  projectName?: string
}

// Helper function to flatten project tree (including subprojects)
function flattenProjects(projects: EnhancedProject[]): EnhancedProject[] {
  const flattened: EnhancedProject[] = []

  function traverse(projectList: EnhancedProject[]) {
    for (const project of projectList) {
      flattened.push(project)
      if (project.children && project.children.length > 0) {
        traverse(project.children)
      }
    }
  }

  traverse(projects)
  return flattened
}

export function SprintListView({ projectId, showProjectSelector = false }: SprintListViewProps) {
  const t = useTranslations('sprintsPage')
  const [createSidebarOpen, setCreateSidebarOpen] = useState(false)
  const [editSidebarOpen, setEditSidebarOpen] = useState(false)
  const [selectedSprint, setSelectedSprint] = useState<DbSprint | null>(null)
  const [projectSearch, setProjectSearch] = useState('')
  const [sprintSearch, setSprintSearch] = useState('')
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [sprintToDelete, setSprintToDelete] = useState<DbSprint | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [addTaskModalOpen, setAddTaskModalOpen] = useState(false)
  const [sprintForAddTask, setSprintForAddTask] = useState<DbSprint | null>(null)
  const { toast } = useToast()

  // Project selector state
  const [selectedProjectId, setSelectedProjectId] = useState<string>(() => {
    if (projectId) return projectId.toString()
    if (typeof window !== 'undefined' && showProjectSelector) {
      return localStorage.getItem('sprints-selected-project') || 'all'
    }
    return 'all'
  })

  // Save selected project to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined' && showProjectSelector && !projectId) {
      localStorage.setItem('sprints-selected-project', selectedProjectId)
    }
  }, [selectedProjectId, showProjectSelector, projectId])

  // Load projects if showing project selector
  const { data: projectsData, isLoading: projectsLoading } = useProjects()
  const projectsHierarchy = projectsData?.projects || []

  // Flatten the project tree to include all projects and subprojects
  const allProjects = useMemo(() => flattenProjects(projectsHierarchy), [projectsHierarchy])

  // Create a map of project ID to project name
  const projectMap = useMemo(() => new Map(allProjects.map(p => [p.id, p.name])), [allProjects])

  // Filter projects based on search
  const filteredProjects = allProjects.filter((project) =>
    project.name.toLowerCase().includes(projectSearch.toLowerCase()) ||
    project.identifier.toLowerCase().includes(projectSearch.toLowerCase())
  )

  // Determine effective project ID
  const effectiveProjectId: number | null = showProjectSelector
    ? (selectedProjectId !== 'all' ? parseInt(selectedProjectId) : null)
    : (projectId ?? null)

  // Use centralized hooks for sprint fetching
  const { 
    sprints: singleProjectSprints, 
    loading: singleProjectLoading, 
    refetch: refetchSingleProject 
  } = useSprints(effectiveProjectId)

  const allProjectIds = useMemo(() => allProjects.map(p => p.id), [allProjects])
  const projectNamesMap = useMemo(() => 
    Object.fromEntries(allProjects.map(p => [p.id, p.name])), 
    [allProjects]
  )

  const { 
    sprints: allProjectsSprints, 
    loading: allProjectsLoading, 
    refetch: refetchAllProjects 
  } = useSprintsFromMultipleProjects(
    effectiveProjectId === null ? allProjectIds : null,
    undefined,
    projectNamesMap
  )

  // Determine which data source to use
  const sprints = effectiveProjectId !== null 
    ? singleProjectSprints.map(sprint => ({
        ...sprint,
        projectName: sprint.projectName || projectMap.get(sprint.projectId)
      }))
    : allProjectsSprints

  const loading = effectiveProjectId !== null ? singleProjectLoading : allProjectsLoading

  const refetch = effectiveProjectId !== null ? refetchSingleProject : refetchAllProjects

  // Filter sprints by search term
  const filteredSprints = sprints.filter(sprint => {
    if (!sprintSearch.trim()) return true
    const searchLower = sprintSearch.toLowerCase()
    return sprint.name.toLowerCase().includes(searchLower) ||
           sprint.projectName?.toLowerCase().includes(searchLower)
  })

  // Group sprints by project
  const sprintsByProject = filteredSprints.reduce((acc, sprint) => {
    const projectName = sprint.projectName || 'Unknown Project'
    if (!acc[projectName]) {
      acc[projectName] = []
    }
    acc[projectName].push(sprint)
    return acc
  }, {} as Record<string, SprintWithProject[]>)

  const handleProjectSelect = (value: string) => {
    setSelectedProjectId(value)
    setProjectSearch('')
  }

  const handleProjectSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProjectSearch(e.target.value)
  }

  const handleEditSprint = (sprint: DbSprint) => {
    setSelectedSprint(sprint)
    setEditSidebarOpen(true)
  }

  const handleCloseEditModal = () => {
    setEditSidebarOpen(false)
    setSelectedSprint(null)
  }

  const handleDeleteClick = (sprint: DbSprint) => {
    setSprintToDelete(sprint)
    setDeleteConfirmOpen(true)
  }

  const handleAddTaskClick = (sprint: DbSprint) => {
    setSprintForAddTask(sprint)
    setAddTaskModalOpen(true)
  }

  const handleAddTaskSuccess = () => {
    setAddTaskModalOpen(false)
    setSprintForAddTask(null)
    refetch()
    toast({
      title: 'Success',
      description: 'Tasks added to sprint successfully',
      variant: 'default'
    })
  }

  const handleDeleteConfirm = async () => {
    if (!sprintToDelete) return

    setIsDeleting(true)
    try {
      const response = await fetch(`/api/db/sprints/${sprintToDelete.id}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        toast({
          title: 'Sprint deleted',
          description: `${sprintToDelete.name} has been deleted successfully.`
        })

        refetch()
        setDeleteConfirmOpen(false)
        setSprintToDelete(null)
      } else {
        const error = await response.json()
        toast({
          title: 'Error',
          description: error.error || 'Failed to delete sprint',
          variant: 'destructive'
        })
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete sprint',
        variant: 'destructive'
      })
    } finally {
      setIsDeleting(false)
    }
  }

  const getStatusIcon = (status: DbSprint['status']) => {
    switch (status) {
      case 1: // active
        return <PlayCircle className="h-4 w-4" />
      case 2: // closed
        return <CheckCircle2 className="h-4 w-4" />
      default: // 0 = open
        return <Clock className="h-4 w-4" />
    }
  }

  const getStatusBadge = (status: DbSprint['status']) => {
    const badgeConfig: Record<number, { className: string; label: string }> = {
      0: { // open
        className: 'border-gray-500 text-gray-700 dark:border-gray-400 dark:text-gray-300',
        label: 'Open'
      },
      1: { // active
        className: 'border-green-600 text-green-700 dark:border-green-500 dark:text-green-400',
        label: 'Active'
      },
      2: { // closed
        className: 'border-blue-600 text-blue-700 dark:border-blue-500 dark:text-blue-400',
        label: 'Closed'
      }
    }

    const config = badgeConfig[status]
    
    // Fallback for unknown status
    if (!config) {
      return (
        <Badge variant="outline" className="border-gray-400 text-gray-600">
          Unknown
        </Badge>
      )
    }

    return (
      <Badge variant="outline" className={config.className}>
        {config.label}
      </Badge>
    )
  }

  const getSprintProgress = (sprint: DbSprint) => {
    const now = new Date()
    const start = sprint.startDate ? new Date(sprint.startDate) : null
    const end = sprint.endDate ? new Date(sprint.endDate) : null

    if (!start || !end) {
      return { daysRemaining: 0, status: 'unknown', label: 'No dates' }
    }

    if (now < start) {
      return { daysRemaining: differenceInDays(start, now), status: 'upcoming', label: 'Starts in' }
    }

    if (now > end) {
      return { daysRemaining: 0, status: 'ended', label: 'Ended' }
    }

    const daysRemaining = differenceInDays(end, now)
    return { daysRemaining, status: 'active', label: 'Days remaining' }
  }

  if (!showProjectSelector && !projectId) {
    return (
      <div className="flex items-center justify-center p-12">
        <p className="text-muted-foreground">Please select a project to view sprints</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Filters and Project Selector */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex-1 flex items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('searchSprintsOrProjects')}
              value={sprintSearch}
              onChange={(e) => setSprintSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Project Selector - Right Side */}
        <div className="flex items-center gap-4">
          {showProjectSelector && (
            <Select
              value={selectedProjectId}
              onValueChange={handleProjectSelect}
            >
              <SelectTrigger className="w-[280px]">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <FolderOpen className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="truncate">
                    <SelectValue placeholder={t('allProjects')} />
                  </div>
                </div>
              </SelectTrigger>
              <SelectContent>
                <div className="p-2 border-b">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder={t('searchProjects')}
                      value={projectSearch}
                      onChange={handleProjectSearchChange}
                      className="pl-8 h-8"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      onPointerDown={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
                <SelectItem value="all">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">All Projects</span>
                  </div>
                </SelectItem>
                {filteredProjects.map((project) => (
                  <SelectItem key={project.id} value={project.id.toString()}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-medium truncate">{project.name}</span>
                      {project.parent && (
                        <span className="text-xs text-muted-foreground truncate">
                          ({project.parent.name})
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
                {filteredProjects.length === 0 && projectSearch && (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    No projects found
                  </div>
                )}
              </SelectContent>
            </Select>
          )}

          {effectiveProjectId && (
            <Button onClick={() => setCreateSidebarOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Sprint
            </Button>
          )}
        </div>
      </div>

      {/* Sprint Groups by Project */}
      {loading || projectsLoading ? (
        <div className="space-y-8">
          {[1, 2].map((groupIndex) => (
            <div key={groupIndex} className="space-y-4">
              {/* Project Header Skeleton */}
              <div className="flex items-center gap-3 px-2">
                <div className="h-5 w-5 bg-muted rounded animate-pulse" />
                <div className="h-6 w-48 bg-muted rounded animate-pulse" />
                <div className="h-6 w-20 bg-muted rounded animate-pulse ml-2" />
              </div>

              {/* Table Skeleton */}
              <div className="overflow-x-auto rounded-md border">
                <table className="min-w-full divide-y divide-border text-sm">
                  <thead className="bg-background">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground">Sprint Name</th>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground w-36">Status</th>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground">Dates</th>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden md:table-cell w-32">Progress</th>
                      <th className="px-4 py-3 text-center font-medium text-muted-foreground w-32">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-border">
                    {[1, 2, 3].map((rowIndex) => (
                      <tr key={rowIndex}>
                        <td className="px-4 py-3 align-middle">
                          <div className="h-5 w-40 bg-muted rounded animate-pulse" />
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <div className="h-6 w-20 bg-muted rounded animate-pulse" />
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <div className="h-4 w-48 bg-muted rounded animate-pulse" />
                        </td>
                        <td className="px-4 py-3 align-middle hidden md:table-cell">
                          <div className="h-4 w-24 bg-muted rounded animate-pulse" />
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <div className="flex items-center justify-center gap-1">
                            <div className="h-8 w-8 bg-muted rounded animate-pulse" />
                            <div className="h-8 w-8 bg-muted rounded animate-pulse" />
                            <div className="h-8 w-8 bg-muted rounded animate-pulse" />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      ) : !effectiveProjectId && showProjectSelector && sprints.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center p-12">
            <FolderOpen className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Select a Project</h3>
            <p className="text-muted-foreground text-center">
              Please select a project from the dropdown to view its sprints, or view all projects' sprints
            </p>
          </CardContent>
        </Card>
      ) : sprints.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center p-12">
            <Zap className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No sprints found</h3>
            <p className="text-muted-foreground text-center mb-4">
              Get started by creating your first sprint
            </p>
            {effectiveProjectId && (
              <Button onClick={() => setCreateSidebarOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create First Sprint
              </Button>
            )}
          </CardContent>
        </Card>
      ) : filteredSprints.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center p-12">
            <Search className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No matching sprints</h3>
            <p className="text-muted-foreground text-center mb-4">
              No sprints match your search "{sprintSearch}". Try a different search term.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {Object.entries(sprintsByProject).map(([projectName, projectSprints]) => (
            <div key={projectName} className="space-y-4">
              {/* Project Header */}
              <div className="flex items-center gap-3 px-2">
                <FolderOpen className="h-5 w-5 text-muted-foreground" />
                <h2 className="text-lg font-semibold">{projectName}</h2>
                <Badge variant="secondary" className="ml-2">
                  {projectSprints.length} {projectSprints.length === 1 ? 'sprint' : 'sprints'}
                </Badge>
              </div>

              {/* Sprints Table */}
              <div className="overflow-x-auto rounded-md border">
                <table className="min-w-full divide-y divide-border text-sm">
                  <thead className="bg-background">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground">Sprint Name</th>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground w-36">Status</th>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground">Dates</th>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden md:table-cell w-32">Progress</th>
                      <th className="px-4 py-3 text-center font-medium text-muted-foreground w-32">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-border">
                    {projectSprints.map((sprint) => {
                      const progress = getSprintProgress(sprint)

                      return (
                        <tr
                          key={sprint.id}
                          className="hover:bg-muted/5 cursor-pointer"
                          onClick={() => {
                            const detailsUrl = `/sprints/${sprint.id}`
                            window.location.href = detailsUrl
                          }}
                        >
                          <td className="px-4 py-3 align-middle">
                            <div className="font-medium">{sprint.name}</div>
                          </td>
                          <td className="px-4 py-3 align-middle">
                            {getStatusBadge(sprint.status)}
                          </td>
                          <td className="px-4 py-3 align-middle text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {sprint.startDate && sprint.endDate ? (
                                <>{format(new Date(sprint.startDate), 'MMM d')} - {format(new Date(sprint.endDate), 'MMM d, yyyy')}</>
                              ) : (
                                <span className="text-muted-foreground">No dates</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 align-middle hidden md:table-cell">
                            {sprint.status === 1 && (
                              <div className="text-sm">
                                <span className="text-muted-foreground">{progress.daysRemaining} days left</span>
                              </div>
                            )}
                            {sprint.status === 0 && (
                              <div className="text-sm text-muted-foreground">
                                Starts in {progress.daysRemaining} days
                              </div>
                            )}
                            {sprint.status === 2 && (
                              <div className="text-sm text-muted-foreground">Closed</div>
                            )}
                          </td>
                          <td className="px-4 py-3 align-middle">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleAddTaskClick(sprint)
                                }}
                                className="h-8 w-8 p-0"
                                title="Add tasks to sprint"
                              >
                                <UserPlus className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  const detailsUrl = `/sprints/${sprint.id}`
                                  window.location.href = detailsUrl
                                }}
                                className="h-8 w-8 p-0"
                                title={t('viewSprintDetails')}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleEditSprint(sprint)
                                }}
                                className="h-8 w-8 p-0"
                                title={t('editSprint')}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleDeleteClick(sprint)
                                }}
                                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                title={t('deleteSprint')}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Sidebars and Modals */}
      <CreateSprintSidebar
        isOpen={createSidebarOpen}
        onClose={() => setCreateSidebarOpen(false)}
        projectId={effectiveProjectId}
        onSuccess={refetch}
      />

      <EditSprintSidebar
        isOpen={editSidebarOpen}
        onClose={handleCloseEditModal}
        sprint={selectedSprint}
        projectId={effectiveProjectId}
        onSuccess={refetch}
        onDelete={() => {
          refetch()
          handleCloseEditModal()
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sprint</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{sprintToDelete?.name}</strong>?
              This action cannot be undone. All issue assignments and story points for this sprint will be removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Task to Sprint Modal */}
      {sprintForAddTask && (
        <AddTaskToSprintModal
          isOpen={addTaskModalOpen}
          onClose={() => {
            setAddTaskModalOpen(false)
            setSprintForAddTask(null)
          }}
          projectId={sprintForAddTask.projectId}
          sprintId={sprintForAddTask.id.toString()}
          onSuccess={handleAddTaskSuccess}
        />
      )}
    </div>
  )
}
